import React from 'react';
import { X, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { proposalTokens } from './tokens';

interface Event {
  id: string;
  title: string;
  tag: string;
  image: string;
  description?: string;
  participants?: string;
  pricePerPerson?: number;
}

interface EventDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
}

export function EventDetailsModal({ 
  isOpen, 
  onClose, 
  event 
}: EventDetailsModalProps) {
  if (!isOpen || !event) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className={cn(
          "w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] animate-fade-in relative",
          "bg-cream"
        )}
        style={{ backgroundColor: proposalTokens.colors.cream }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-md bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
        </button>

        {/* Content Container - Scrollable */}
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Image */}
          <div className="relative h-[250px] md:h-[300px] w-full bg-black overflow-hidden">
            <img 
              src={event.image} 
              className="w-full h-full object-cover" 
              alt={event.title}
            />
            {/* Tag Badge */}
            <div className="absolute top-4 left-4">
              <span 
                className="text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" 
                style={{ backgroundColor: proposalTokens.colors.gold }}
              >
                {event.tag}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Title */}
            <h2 
              className="text-2xl md:text-3xl font-serif font-bold mb-4" 
              style={{ 
                color: proposalTokens.colors.teal, 
                fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') 
              }}
            >
              {event.title}
            </h2>

            {/* Description */}
            {event.description && (
              <div className="mb-6">
                <p 
                  className="text-base md:text-lg leading-relaxed whitespace-pre-wrap" 
                  style={{ color: `${proposalTokens.colors.teal}99` }}
                >
                  {event.description}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-black/10">
              {event.participants && (
                <div className="flex items-center text-sm md:text-base" style={{ color: `${proposalTokens.colors.teal}80` }}>
                  <Users className="w-4 h-4 mr-2" />
                  <span className="font-medium mr-2">Participants:</span>
                  <span>{event.participants}</span>
                </div>
              )}
              {event.pricePerPerson && (
                <div className="flex items-center text-sm md:text-base" style={{ color: proposalTokens.colors.teal }}>
                  <span className="font-medium mr-2">Price:</span>
                  <span className="font-bold">${event.pricePerPerson}/person</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

